import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PickersDayProps as MuiPickersDayProps } from '@mui/x-date-pickers';
import { isSameDay, isWithinInterval, isAfter } from 'date-fns';
import { PickersDay } from './PickersDay';
import { generateNewDateWithCurrentTime } from './util';
import { Badge, Tooltip, Typography } from '@mui/material';
import moment from 'moment';

export type DateRange = {
  start: Date | null;
  end: Date | null;
};

export type Event = {
  start: Date | null;
  end: Date | null;
  title: string;
  tag?: string;
  onClick: Function;
};

export type DateRangeItem = DateRange & {
  id: number;
};

export interface DateRanges {
  default: Array<DateRangeItem>;
  reply?: Array<DateRangeItem>;
  suggest?: Array<DateRangeItem>;
}

export type Mode = 'default' | 'reply' | 'suggest';

type RenderDay = (
  date: Date,
  selectedDates: Array<Date | null>,
  pickersDayProps: MuiPickersDayProps<Date>
) => JSX.Element;

type DateRangesInputContextType = {
  disabled?: boolean;
  reply?: boolean;
  single?: boolean;
  events?: Array<Event>;
  /**
   * Manage date ranges
   */
  dateRanges: DateRanges;
  setDateRanges: (value: DateRanges) => void;
  /**
   * Manage active date range
   */
  activeDateRange: {
    mode: Mode;
    index?: number;
  };
  setActiveDateRange: (value: { mode: Mode; index: number }) => void;
  /**
   * Last selected date
   */
  value: Date | null;

  /**
   * Handle date range change
   */
  handleChange: (value: any, keyboardInputValue?: string | undefined) => void;

  /**
   * Custom render days
   */
  renderDay: RenderDay;
  showInstructions: boolean;
  setShowInstructions: (show: boolean) => void;
  activeStep: number;
  setActiveStep: (step: number) => void;
};

const defaultDateRangesInputContextValue = {
  disabled: false,
  reply: false,
  single: false,
  dateRanges: { default: [] },
  events: [],
  setDateRanges: () => {},
  activeDateRange: { mode: 'default' as Mode },
  setActiveDateRange: () => {},
  value: null,
  handleChange: () => {},
  renderDay: () => <></>,
  showInstructions: false,
  setShowInstructions: () => {},
  activeStep: 0,
  setActiveStep: () => {},
};

export const DateRangesInputContext = createContext<DateRangesInputContextType>(
  defaultDateRangesInputContextValue
);

interface DateRangesInputProviderProps {
  disabled?: boolean;
  reply?: boolean;
  single?: boolean;
  children?: any;
  dateRanges: DateRanges;
  events?: Array<Event>;
  onChange?: (range: DateRange | null, ranges: DateRanges) => void;
}

export const DateRangesInputProvider = ({
  disabled = false,
  reply = false,
  single = false,
  children = null,
  dateRanges: _dateRanges = { default: [] },
  events = [],
  onChange = () => {},
}: DateRangesInputProviderProps) => {
  const initialDateRanges = {
    ...(_dateRanges || []),
    default: _dateRanges?.default?.map((item, index) => ({
      ...item,
      id: index + 1,
    })),
    suggest:
      reply &&
      (_dateRanges?.default?.length === 0 ||
        !_dateRanges?.default?.[0]?.start ||
        !moment(_dateRanges?.default?.[0]?.start).isValid())
        ? [{ start: null, end: null, id: 1 }]
        : undefined,
  };

  const initialActiveDateRange: {
    mode: Mode;
    index?: number;
  } =
    initialDateRanges.default.length > 0
      ? {
          mode: reply
            ? initialDateRanges?.default?.[0]?.start &&
              moment(initialDateRanges?.default?.[0]?.start).isValid()
              ? 'reply'
              : 'suggest'
            : 'default',
          index: initialDateRanges.default[0].id,
        }
      : { mode: reply ? 'reply' : ('default' as Mode) };

  const [{ dateRanges = { default: [] }, activeDateRange }, setContextValue] =
    useState<
      Omit<
        DateRangesInputContextType,
        | 'value'
        | 'handleChange'
        | 'renderDay'
        | 'showInstructions'
        | 'setShowInstructions'
        | 'activeStep'
        | 'setActiveStep'
      >
    >({
      dateRanges: initialDateRanges,
      activeDateRange: initialActiveDateRange,
      setDateRanges: () => {},
      setActiveDateRange: () => {},
    });

  const setDateRanges = (dateRanges: DateRanges) => {
    setContextValue(prev => ({ ...prev, dateRanges: dateRanges }));
  };

  const setActiveDateRange = (activeDateRange: {
    mode: Mode;
    index?: number;
  }) => {
    const activeRange = dateRanges?.[
      activeDateRange.mode !== 'reply' ? activeDateRange.mode : 'default'
    ]?.find(r => r.id === activeDateRange.index);
    setValue(activeRange?.start || null);
    setContextValue(prev => ({ ...prev, activeDateRange }));
  };

  useEffect(() => {
    const range =
      dateRanges[activeDateRange.mode]?.find(
        item => item.id === activeDateRange.index
      ) || null;
    const ranges = dateRanges;
    onChange(range, ranges);
  }, [dateRanges, activeDateRange]);

  const [value, setValue] = useState<Date | null>(
    dateRanges?.[
      activeDateRange.mode !== 'reply' ? activeDateRange.mode : 'default'
    ]?.find(r => r.id === activeDateRange.index)?.start || null
  );

  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<number>(0);
  const dateRange = dateRanges?.[
    activeDateRange?.mode === 'reply'
      ? 'default'
      : activeDateRange?.mode || 'default'
  ]?.find(r => r.id === activeDateRange.index); //[activeDateRange.index || 0];

  useEffect(() => {
    if (value) {
      setValue(null); //Need to reset value to null so handleChange fires
    }
  }, [value]);

  const { start, end, selectedStart, selectedEnd } = useMemo(() => {
    const activeRange = dateRanges?.default?.find(
      r => r.id === activeDateRange.index
    );
    const start = activeRange?.start;
    const end = activeRange?.end;

    const selectedRange =
      activeDateRange.mode !== 'default'
        ? dateRanges[activeDateRange.mode]?.find(
            r => r.id === activeDateRange.index
          )
        : undefined;

    const selectedStart = selectedRange?.start;
    const selectedEnd = selectedRange?.end;
    return {
      start: start || null,
      end: end || null,
      selectedStart: selectedStart || null,
      selectedEnd: selectedEnd || null,
    };
  }, [dateRanges, activeDateRange]);

  const renderDay = (
    date: Date,
    selectedDates: Array<Date | null>,
    pickersDayProps: MuiPickersDayProps<Date>
  ) => {
    const dayIsBetween =
      start && end && isAfter(end, start)
        ? isWithinInterval(date, { start, end })
        : false;
    const isFirstDay = start && isSameDay(date, start) ? true : false;
    const isLastDay = end && isSameDay(date, end) ? true : false;
    const daysEvents =
      events?.filter(e => {
        const start = e.start && new Date(e.start);
        const end = e.end && new Date(e.end);
        const dayIsBetween =
          start && end && isAfter(end, start) && date
            ? isWithinInterval(date, { start, end })
            : false;
        const isFirstDay = start && isSameDay(date, start) ? true : false;
        const isLastDay = end && isSameDay(date, end) ? true : false;
        return dayIsBetween || isFirstDay || isLastDay;
      }) || [];
    const isEventDay = daysEvents.length > 0;
    const selectedDayIsBetween =
      selectedStart && selectedEnd && isAfter(selectedEnd, selectedStart)
        ? isWithinInterval(date, { start: selectedStart, end: selectedEnd })
        : false;
    const selectedIsFirstDay =
      selectedStart && isSameDay(date, selectedStart) ? true : false;
    const selectedIsLastDay =
      selectedEnd && isSameDay(date, selectedEnd) ? true : false;
    return (
      <Tooltip
        title={
          !reply && daysEvents.length > 0 ? (
            <div>
              <div>
                <u>
                  <b>The following item(s) are on this date:</b>
                </u>
              </div>
              {daysEvents.map(e => (
                <div>{e.title}</div>
              ))}
            </div>
          ) : (
            ''
          )
        }
      >
        <Badge
          key={pickersDayProps?.day?.toString()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          overlap="circular"
          badgeContent={
            !reply && isFirstDay ? (
              <Typography
                variant="inherit"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme => theme.palette.secondary.main,
                  backgroundColor: 'white',
                  border: theme => `1px solid ${theme.palette.secondary.main}`,
                  px: '2px',
                  py: '1px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                }}
              >
                START
              </Typography>
            ) : !reply && isLastDay ? (
              <Typography
                variant="inherit"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme => theme.palette.secondary.main,
                  backgroundColor: 'white',
                  border: theme => `1px solid ${theme.palette.secondary.main}`,
                  px: '2px',
                  py: '1px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                }}
              >
                END
              </Typography>
            ) : undefined
          }
        >
          <PickersDay
            {...pickersDayProps}
            selected={false} //handle clicks differently
            today={pickersDayProps.today && !reply}
            disabled={
              reply && (activeDateRange.mode === 'suggest' || dayIsBetween)
                ? false
                : disabled || reply
            }
            dayIsBetween={dayIsBetween}
            isFirstDay={isFirstDay}
            isLastDay={isLastDay}
            isEventDay={isEventDay}
            selectedDayIsBetween={selectedDayIsBetween}
            selectedIsFirstDay={selectedIsFirstDay}
            selectedIsLastDay={selectedIsLastDay}
            reply={reply}
          />
        </Badge>
      </Tooltip>
    );
  };

  const handleChange = (newValue: any) => {
    if (disabled) return;
    if (newValue) {
      const activeRange = dateRanges[activeDateRange.mode]?.find(
        r => r.id === activeDateRange.index
      );
      const start = activeRange?.start;
      const end = activeRange?.end;
      let newRange = {
        id: activeDateRange.index,
        start: newValue,
        end: null,
      };
      if (start && !end && newValue >= start) {
        newRange = {
          id: activeDateRange.index,
          start: start,
          end: newValue,
        };
      }
      setContextValue(prev => {
        return {
          ...prev,
          dateRanges: {
            ...prev.dateRanges,
            [activeDateRange.mode]: [
              ...(prev.dateRanges[activeDateRange.mode] || []).filter(
                r => r.id !== activeDateRange.index
              ),
              newRange,
            ],
          },
        };
      });
    }
  };

  const updateDateRangeItem = (value: DateRangeItem) => {
    if (reply && activeDateRange.mode === 'reply' && activeDateRange.index) {
      let index = dateRanges.reply?.findIndex(item => item.id === value.id);
      if (index === undefined || index < 0) {
        index === activeDateRange.index;
        //need to add
        setDateRanges({
          ...dateRanges,
          reply: [...(dateRanges.reply || []), value],
        });
        setActiveDateRange({ mode: 'reply', index: activeDateRange.index });
      } else if (index !== undefined && index >= 0) {
        let updatedMode = [...(dateRanges[activeDateRange.mode] || [])];
        if (updatedMode.length > index) {
          updatedMode[index] = value;
          setDateRanges({
            ...dateRanges,
            [activeDateRange.mode]: updatedMode,
          });
        }
      }
    } else {
      const index = dateRanges[activeDateRange.mode]?.findIndex(
        item => item.id === value.id
      );
      if (index !== undefined && index >= 0) {
        let updatedMode = [...(dateRanges[activeDateRange.mode] || [])];
        if (updatedMode.length > index) {
          updatedMode[index] = value;
          setDateRanges({
            ...dateRanges,
            [activeDateRange.mode]: updatedMode,
          });
        }
      }
    }
  };

  const onDateChange = ({
    start,
    end,
  }: {
    start: Date | null;
    end: Date | null;
  }) => {
    const { id } = dateRanges[activeDateRange.mode]?.find(
      item => item.id === activeDateRange?.index
    ) || {
      start: null,
      end: null,
    };
    if (disabled) return;
    if (id && start) {
      updateDateRangeItem({ id, start, end });
    } else if (activeDateRange.index && start) {
      updateDateRangeItem({ id: activeDateRange.index, start, end });
    }
  };

  return (
    <DateRangesInputContext.Provider
      value={{
        disabled,
        reply,
        single,
        dateRanges,
        events,
        activeDateRange,
        setDateRanges,
        setActiveDateRange,
        handleChange,
        value,
        renderDay,
        showInstructions,
        setShowInstructions,
        activeStep,
        setActiveStep,
      }}
    >
      {children}
    </DateRangesInputContext.Provider>
  );
};

export const useDateRangesInputContext = () => {
  return useContext(DateRangesInputContext);
};
